import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
// Shooting star colors for dark mode - bright and visible
const STAR_COLORS = [
    '#ffffff', // Pure white
    '#ffffff', // Pure white (more common)
    '#e8f4ff', // Ice blue white
    '#fff8e8', // Warm white
];
// Light mode: faster settling drift with downward bias
const LIGHT_LAYER_SPEEDS = [0.4, 0.7, 1.0];
// Dark mode: slow gentle drift
const DARK_LAYER_SPEEDS = [0.15, 0.3, 0.5];
// Desktop particle counts
const LIGHT_PARTICLE_COUNTS = [100, 80, 60];
const DARK_PARTICLE_COUNTS = [50, 40, 30];
// Mobile particle counts (reduced for performance)
const LIGHT_PARTICLE_COUNTS_MOBILE = [25, 15, 10];
const DARK_PARTICLE_COUNTS_MOBILE = [25, 20, 15];
// Birthday cake sprinkle colors - vibrant festive pastels
const SPRINKLE_COLORS = [
    'rgba(255, 100, 130, 0.95)', // Hot pink
    'rgba(255, 160, 80, 0.95)', // Bright orange
    'rgba(255, 220, 50, 0.95)', // Sunny yellow
    'rgba(100, 220, 150, 0.9)', // Mint green
    'rgba(100, 180, 255, 0.9)', // Sky blue
    'rgba(200, 130, 255, 0.9)', // Purple
    'rgba(255, 130, 170, 0.95)', // Coral pink
    'rgba(255, 200, 100, 0.95)', // Gold
];
// Ice cream drip color - matches the cream background exactly
const CREAM_COLOR = '#FFF5EB';
export default function AnimatedBackground({ theme }) {
    const canvasRef = useRef(null);
    const animationRef = useRef(0);
    const particlesRef = useRef([]);
    const dripsRef = useRef([]);
    const shootingStarsRef = useRef([]);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const lastDripTime = useRef(-1); // -1 means uninitialized
    const lastShootingStarTime = useRef(-1); // -1 means uninitialized
    const nextShootingStarDelay = useRef(1000); // Pre-calculated delay
    const initialCanvasSize = useRef(null); // Track initial size to avoid resize on scroll
    // Use prop theme, or fall back to system preference
    const [systemDarkMode, setSystemDarkMode] = useState(false);
    const isDarkMode = theme ? theme === 'dark' : systemDarkMode;
    const isDarkModeRef = useRef(isDarkMode);
    const isMobileRef = useRef(isMobile);
    // Keep ref in sync with current theme and force immediate redraw
    useEffect(() => {
        isDarkModeRef.current = isDarkMode;
        isMobileRef.current = isMobile;
        // Force immediate canvas redraw when theme changes
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                // Draw the correct background immediately
                if (isDarkMode) {
                    ctx.fillStyle = '#1a1f2e';
                }
                else {
                    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
                    gradient.addColorStop(0, '#FFF8F0');
                    gradient.addColorStop(0.3, '#FFF5EB');
                    gradient.addColorStop(0.6, '#FFF0E8');
                    gradient.addColorStop(1, '#FFF5F0');
                    ctx.fillStyle = gradient;
                }
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [isDarkMode]);
    // Check for mobile device (for performance optimization)
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    // Check for reduced motion preference
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);
    // Check for system dark mode (fallback if no prop)
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setSystemDarkMode(mediaQuery.matches);
        const handler = (e) => setSystemDarkMode(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);
    // Initialize particles based on theme
    const initParticles = (width, height, darkMode, mobile) => {
        const particles = [];
        // Use reduced particle counts on mobile for better performance
        const counts = mobile
            ? (darkMode ? DARK_PARTICLE_COUNTS_MOBILE : LIGHT_PARTICLE_COUNTS_MOBILE)
            : (darkMode ? DARK_PARTICLE_COUNTS : LIGHT_PARTICLE_COUNTS);
        const speeds = darkMode ? DARK_LAYER_SPEEDS : LIGHT_LAYER_SPEEDS;
        for (let layer = 0; layer < 3; layer++) {
            const count = counts[layer] ?? 30;
            for (let i = 0; i < count; i++) {
                particles.push(createParticle(width, height, layer, darkMode, speeds));
            }
        }
        particlesRef.current = particles;
    };
    const createParticle = (width, height, layer, darkMode, speeds) => {
        // Bigger particles - more visible
        const baseSize = layer === 0 ? 2.5 : layer === 1 ? 4 : 5.5;
        const sizeVariation = Math.random() * 2;
        const colorIndex = Math.floor(Math.random() * SPRINKLE_COLORS.length);
        const starTypes = ['plus', 'asterisk', 'cross'];
        return {
            x: Math.random() * width,
            y: Math.random() * height,
            size: baseSize + sizeVariation,
            opacity: darkMode
                ? 0.5 + Math.random() * 0.5 * (layer === 2 ? 1 : layer === 1 ? 0.9 : 0.7)
                : 0.7 + Math.random() * 0.3,
            speed: speeds[layer] ?? 0.3,
            layer,
            color: SPRINKLE_COLORS[colorIndex] ?? 'rgba(255, 100, 130, 0.95)',
            rotation: Math.random() * 360,
            rotationSpeed: darkMode ? 0 : (Math.random() - 0.5) * 1.2,
            twinklePhase: Math.random() * Math.PI * 2,
            starType: starTypes[Math.floor(Math.random() * starTypes.length)],
        };
    };
    // Helper: rounded rectangle for broad browser support (fallback if ctx.roundRect missing)
    const roundRect = (ctx, x, y, w, h, r) => {
        if (ctx.roundRect) {
            ctx.roundRect(x, y, w, h, r);
            return;
        }
        const radius = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + w - radius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
        ctx.lineTo(x + w, y + h - radius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
        ctx.lineTo(x + radius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    };
    // Create cream drip that emerges and grows from the background
    const createDrip = (width, _height) => {
        // Wider drips: 55-105px
        const dripWidth = 55 + Math.random() * 50;
        // Generate sprinkles embedded in the drip
        const sprinkleCount = 2 + Math.floor(Math.random() * 4);
        const sprinkles = [];
        for (let i = 0; i < sprinkleCount; i++) {
            const sprinkleColorIdx = Math.floor(Math.random() * SPRINKLE_COLORS.length);
            sprinkles.push({
                offsetX: (Math.random() - 0.5) * dripWidth * 0.5,
                offsetY: 50 + Math.random() * 150, // Spread along the drip length
                color: SPRINKLE_COLORS[sprinkleColorIdx] ?? 'rgba(255, 100, 130, 0.95)',
                size: 3 + Math.random() * 3,
                rotation: Math.random() * 360,
            });
        }
        return {
            x: 40 + Math.random() * (width - 80),
            y: 0, // Start at top edge of screen
            speed: 1.2 + Math.random() * 1.3,
            width: dripWidth,
            baseColor: CREAM_COLOR,
            opacity: 1,
            life: 0,
            maxLife: 500 + Math.random() * 500,
            // Start in growth phase - drip emerges from background
            growthPhase: 'growing',
            currentLength: 5, // Start very small
            maxGrowthLength: 80 + Math.random() * 120, // Grow to this length before dripping
            sprinkles,
        };
    };
    // Create realistic shooting star
    const createShootingStar = (width, height) => {
        // Start from various positions across the top and right edges
        const startZone = Math.random();
        let startX;
        let startY;
        if (startZone < 0.4) {
            // Start from top edge - spread across entire width
            startX = Math.random() * width;
            startY = -20 + Math.random() * height * 0.1;
        }
        else if (startZone < 0.7) {
            // Start from right edge - spread across more height
            startX = width + 20 + Math.random() * 50;
            startY = Math.random() * height * 0.6;
        }
        else {
            // Start from top-right corner area
            startX = width * (0.6 + Math.random() * 0.4);
            startY = -20 + Math.random() * height * 0.15;
        }
        // Angle: 120-150 degrees = moving down and to the LEFT
        // This creates the classic shooting star diagonal trajectory
        const baseAngle = (125 + Math.random() * 20) * Math.PI / 180;
        const colorIdx = Math.floor(Math.random() * STAR_COLORS.length);
        return {
            x: startX,
            y: startY,
            speed: 8 + Math.random() * 6, // Original moderate speed
            length: 80 + Math.random() * 120,
            width: 1.5 + Math.random() * 1.5, // Original thinner trail
            opacity: 0,
            life: 0,
            maxLife: 120 + Math.random() * 60, // Original longer life for graceful arc
            angle: baseAngle,
            trail: [],
            sparkles: [],
            headGlow: 8 + Math.random() * 4, // Original subtle glow
            color: STAR_COLORS[colorIdx] ?? '#ffffff',
            fadePhase: 'in',
        };
    };
    // Main animation loop
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        // Get initial dimensions - use screen dimensions for stability
        const getStableDimensions = () => {
            // Use document dimensions which are more stable on mobile
            const w = Math.max(1, document.documentElement.clientWidth || window.innerWidth || 1);
            // For height, use the larger of screen height or window height to avoid address bar issues
            const h = Math.max(1, window.screen?.height || window.innerHeight || 1);
            return { w, h };
        };
        // Debounced resize handling - only respond to significant width changes
        let resizeTimeout;
        const resize = (force = false) => {
            // clear previous debounce
            if (resizeTimeout)
                window.clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
                const { w, h } = getStableDimensions();
                // Store initial size on first call
                if (!initialCanvasSize.current) {
                    initialCanvasSize.current = { w, h };
                }
                // Only resize if width changed significantly (ignore height changes from mobile scroll)
                // Height changes from address bar are typically < 100px
                const widthChanged = Math.abs(canvas.width - w) > 10;
                const heightChangedSignificantly = Math.abs(canvas.height - h) > 150;
                if (force || widthChanged || (heightChangedSignificantly && canvas.width === 0)) {
                    canvas.width = w;
                    canvas.height = h;
                    initParticles(canvas.width, canvas.height, isDarkMode, isMobile);
                    // Don't clear effects on minor resizes
                    if (force || widthChanged) {
                        dripsRef.current = [];
                        shootingStarsRef.current = [];
                    }
                }
                resizeTimeout = undefined;
            }, 120);
        };
        const handleResize = () => resize(false);
        resize(true); // Force initial resize
        window.addEventListener('resize', handleResize);
        // Reinitialize particles when theme changes
        initParticles(canvas.width, canvas.height, isDarkMode, isMobile);
        dripsRef.current = [];
        shootingStarsRef.current = [];
        if (prefersReducedMotion) {
            // Static background for reduced motion
            drawStaticBackground(ctx, canvas.width, canvas.height, isDarkMode);
            return () => window.removeEventListener('resize', handleResize);
        }
        let frameCount = 0;
        const animate = (timestamp) => {
            // Ensure canvas still exists and has valid size/context
            const c = canvasRef.current;
            if (!c)
                return;
            const ctxNow = c.getContext('2d');
            if (!ctxNow || c.width <= 0 || c.height <= 0)
                return;
            // ALWAYS draw background first - this is outside try/catch to ensure it always runs
            const currentIsDarkMode = isDarkModeRef.current;
            if (currentIsDarkMode) {
                ctxNow.fillStyle = '#1a1f2e';
            }
            else {
                const gradient = ctxNow.createLinearGradient(0, 0, 0, c.height);
                gradient.addColorStop(0, '#FFF8F0');
                gradient.addColorStop(0.3, '#FFF5EB');
                gradient.addColorStop(0.6, '#FFF0E8');
                gradient.addColorStop(1, '#FFF5F0');
                ctxNow.fillStyle = gradient;
            }
            ctxNow.fillRect(0, 0, c.width, c.height);
            frameCount++;
            // Draw particles and effects - wrapped in try/catch so background still shows on error
            try {
                // Update and draw particles (layer by layer for proper depth)
                for (let layer = 0; layer < 3; layer++) {
                    particlesRef.current
                        .filter(p => p.layer === layer)
                        .forEach(particle => {
                        updateParticle(particle, c.width, c.height, currentIsDarkMode, frameCount);
                        drawParticle(ctxNow, particle, currentIsDarkMode, frameCount, isMobileRef.current);
                    });
                }
                if (currentIsDarkMode) {
                    // Initialize timestamp on first frame
                    if (lastShootingStarTime.current < 0) {
                        lastShootingStarTime.current = timestamp;
                        nextShootingStarDelay.current = 500; // First star comes fairly quickly
                    }
                    // Dark mode: spawn shooting stars periodically
                    if (timestamp - lastShootingStarTime.current > nextShootingStarDelay.current) {
                        if (shootingStarsRef.current.length < 3) {
                            shootingStarsRef.current.push(createShootingStar(c.width, c.height));
                            lastShootingStarTime.current = timestamp;
                            // Pre-calculate next delay (2-5 seconds between stars)
                            nextShootingStarDelay.current = 2000 + Math.random() * 3000;
                        }
                    }
                    // Update and draw shooting stars
                    shootingStarsRef.current = shootingStarsRef.current.filter(star => {
                        updateShootingStar(star, c.width, c.height);
                        // Keep star alive until it's completely off screen
                        if (star.y < c.height + 200 && star.x > -200) {
                            drawShootingStar(ctxNow, star);
                            return true;
                        }
                        return false;
                    });
                }
                else {
                    // Initialize timestamp on first frame
                    if (lastDripTime.current < 0) {
                        lastDripTime.current = timestamp;
                    }
                    // Light mode: spawn cream drips with sprinkles
                    // Reduce drip frequency and count on mobile for performance
                    const dripDelay = isMobileRef.current ? 2000 + Math.random() * 3000 : 600 + Math.random() * 1200;
                    const maxDrips = isMobileRef.current ? 2 : 6;
                    if (timestamp - lastDripTime.current > dripDelay) {
                        if (dripsRef.current.length < maxDrips) {
                            dripsRef.current.push(createDrip(c.width, c.height));
                            lastDripTime.current = timestamp;
                        }
                    }
                    // Update and draw drips
                    dripsRef.current = dripsRef.current.filter(drip => {
                        updateDrip(drip, c.height);
                        if (drip.life < drip.maxLife && drip.opacity > 0) {
                            drawDrip(ctxNow, drip);
                            return true;
                        }
                        return false;
                    });
                }
            }
            catch (err) {
                // Prevent any drawing error from stopping the animation loop
                console.error('AnimatedBackground render error:', err);
            }
            animationRef.current = requestAnimationFrame(animate);
        };
        animationRef.current = requestAnimationFrame(animate);
        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationRef.current);
            // Clear timeout if still pending
            if (resizeTimeout)
                window.clearTimeout(resizeTimeout);
        };
    }, [prefersReducedMotion, isDarkMode]);
    const updateParticle = (particle, width, height, darkMode, frameCount) => {
        if (darkMode) {
            // Dark mode: freeze positional movement so stars form a static sky
            // Only update twinkle phase to make stars shimmer
            if (particle.twinklePhase !== undefined) {
                // Use a speed tied to particle size for natural variety
                particle.twinklePhase += 0.05 + (particle.size % 1) * 0.02;
            }
            // Keep rotation static for stars (no rotation)
        }
        else {
            // Light mode: faster downward settling drift (like sprinkles falling)
            particle.y += particle.speed * 0.8;
            // Gentle side-to-side sway
            particle.x += Math.sin(frameCount * 0.01 + particle.x * 0.015) * particle.speed * 0.5;
            // Rotate sprinkles
            if (particle.rotation !== undefined && particle.rotationSpeed !== undefined) {
                particle.rotation += particle.rotationSpeed;
            }
        }
        // Wrap around only affects moving particles (light mode). Dark-mode stars remain fixed.
        if (!darkMode) {
            if (particle.y > height + 10) {
                particle.y = -10;
                particle.x = Math.random() * width;
            }
        }
        if (particle.x < -10)
            particle.x = width + 10;
        if (particle.x > width + 10)
            particle.x = -10;
    };
    const drawParticle = (ctx, particle, isDark, _frameCount, mobile) => {
        ctx.save();
        ctx.translate(particle.x, particle.y);
        if (isDark) {
            // Dark mode: star-shaped twinkles (plus/asterisk/cross)
            const twinkle = particle.twinklePhase !== undefined
                ? 0.6 + 0.4 * Math.sin(particle.twinklePhase)
                : 1;
            ctx.globalAlpha = particle.opacity * twinkle;
            ctx.fillStyle = '#ffffff';
            ctx.strokeStyle = '#ffffff';
            ctx.shadowBlur = mobile ? 0 : particle.size * 3;
            ctx.shadowColor = 'rgba(200, 220, 255, 0.7)';
            if (particle.rotation !== undefined) {
                ctx.rotate(particle.rotation * Math.PI / 180);
            }
            const size = particle.size;
            ctx.lineWidth = size * 0.3;
            ctx.lineCap = 'round';
            // Diamond-shaped star (rotated square) for a more classic star sparkle
            ctx.globalAlpha = particle.opacity * twinkle;
            ctx.fillStyle = '#ffffff';
            ctx.strokeStyle = '#ffffff';
            ctx.shadowBlur = mobile ? 0 : particle.size * 3;
            ctx.shadowColor = 'rgba(200, 220, 255, 0.7)';
            if (particle.rotation !== undefined) {
                ctx.rotate(particle.rotation * Math.PI / 180);
            }
            const d = size;
            ctx.beginPath();
            ctx.moveTo(0, -d);
            ctx.lineTo(d, 0);
            ctx.lineTo(0, d);
            ctx.lineTo(-d, 0);
            ctx.closePath();
            ctx.fill();
            // Small central glow
            ctx.beginPath();
            ctx.arc(0, 0, Math.max(0.4, d * 0.18), 0, Math.PI * 2);
            ctx.fill();
        }
        else {
            // Light mode: colorful sprinkles
            ctx.globalAlpha = particle.opacity;
            ctx.rotate((particle.rotation || 0) * Math.PI / 180);
            ctx.fillStyle = particle.color ?? 'rgba(255, 150, 170, 0.85)';
            ctx.shadowBlur = 1;
            ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
            // All sprinkles are elongated pill shapes
            const width = particle.size * 0.6;
            const height = particle.size * 2.2;
            ctx.beginPath();
            roundRect(ctx, -width / 2, -height / 2, width, height, width / 2);
            ctx.fill();
        }
        ctx.restore();
    };
    // Update cream drip - growth phase then dripping phase
    const updateDrip = (drip, canvasHeight) => {
        drip.life++;
        if (drip.growthPhase === 'growing') {
            // Drip is emerging from the background - grow longer
            drip.currentLength += 0.8 + Math.random() * 0.4;
            // Once long enough, start dripping
            if (drip.currentLength >= drip.maxGrowthLength) {
                drip.growthPhase = 'dripping';
            }
        }
        else {
            // Dripping phase - move downward
            drip.y += drip.speed;
            // Also slowly increase length while dripping for stretchy effect
            drip.currentLength = Math.min(drip.currentLength + 0.2, 350);
        }
        // Start fading when near end of life
        const fadeStart = drip.maxLife - 150;
        if (drip.life > fadeStart) {
            drip.opacity = Math.max(0, (drip.maxLife - drip.life) / 150);
        }
        // Also fade if drip has gone well past the screen
        if (drip.y > canvasHeight + 50) {
            drip.opacity *= 0.92;
        }
    };
    // Draw cream drip with opacity gradient (transparent at top, solid at bottom)
    const drawDrip = (ctx, drip) => {
        ctx.save();
        // The drip hangs from y=0 (top of screen) and extends down to drip.y + currentLength
        const topY = drip.y;
        const bottomY = drip.y + drip.currentLength;
        const halfWidth = drip.width / 2;
        // Calculate width factor based on length - short drips are narrower
        const lengthFactor = Math.min(1, drip.currentLength / 100);
        const currentHalfWidth = halfWidth * (0.3 + lengthFactor * 0.7);
        // Drip cream color - slightly darker than background for visibility
        const creamColor = { r: 255, g: 240, b: 225 }; // Warmer, slightly more visible cream
        // Create OPACITY gradient - transparent at top, solid at bottom
        const opacityGradient = ctx.createLinearGradient(drip.x, topY, drip.x, bottomY);
        opacityGradient.addColorStop(0, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 0)`); // Fully transparent at top
        opacityGradient.addColorStop(0.15, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 0.1)`); // Very faint
        opacityGradient.addColorStop(0.35, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 0.4)`); // Building up
        opacityGradient.addColorStop(0.6, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 0.7)`); // More solid
        opacityGradient.addColorStop(0.85, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 0.95)`); // Almost full
        opacityGradient.addColorStop(1, `rgba(${creamColor.r}, ${creamColor.g}, ${creamColor.b}, 1)`); // Full opacity at tip
        // Add subtle shadow for depth - makes drip look 3D
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(180, 160, 140, 0.25)';
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 3;
        ctx.globalAlpha = drip.opacity;
        ctx.fillStyle = opacityGradient;
        // Draw the drip shape
        ctx.beginPath();
        // Top attachment point - wide to help blend
        const attachWidth = currentHalfWidth * 1.2;
        ctx.moveTo(drip.x - attachWidth, topY);
        // Narrow down from attachment to neck
        const neckY = topY + Math.min(40, drip.currentLength * 0.25);
        ctx.quadraticCurveTo(drip.x - attachWidth * 0.8, topY + 15, drip.x - currentHalfWidth * 0.5, neckY);
        // Main body - slight taper
        const bodyEndY = bottomY - currentHalfWidth * 1.5;
        ctx.lineTo(drip.x - currentHalfWidth * 0.6, Math.max(neckY, bodyEndY));
        // Bottom bulb/blob - the most visible part
        ctx.quadraticCurveTo(drip.x - currentHalfWidth * 1.0, bottomY, drip.x, bottomY + currentHalfWidth * 0.7);
        ctx.quadraticCurveTo(drip.x + currentHalfWidth * 1.0, bottomY, drip.x + currentHalfWidth * 0.6, Math.max(neckY, bodyEndY));
        // Right side going up
        ctx.lineTo(drip.x + currentHalfWidth * 0.5, neckY);
        // Back to top attachment
        ctx.quadraticCurveTo(drip.x + attachWidth * 0.8, topY + 15, drip.x + attachWidth, topY);
        ctx.closePath();
        ctx.fill();
        // Add a subtle highlight on the blob for 3D effect
        if (drip.currentLength > 50) {
            ctx.shadowBlur = 0;
            ctx.globalAlpha = drip.opacity * 0.4;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.beginPath();
            ctx.ellipse(drip.x - currentHalfWidth * 0.2, bottomY - currentHalfWidth * 0.3, Math.max(2, currentHalfWidth * 0.15), Math.max(3, currentHalfWidth * 0.25), -0.3, 0, Math.PI * 2);
            ctx.fill();
        }
        // Draw sprinkles embedded in the drip (only if drip is long enough)
        if (drip.currentLength > 60) {
            ctx.globalAlpha = drip.opacity;
            drip.sprinkles.forEach(sprinkle => {
                const sprinkleY = topY + sprinkle.offsetY;
                // Only draw sprinkle if it's within the visible drip area
                if (sprinkleY > neckY && sprinkleY < bottomY - 10) {
                    ctx.save();
                    ctx.translate(drip.x + sprinkle.offsetX, sprinkleY);
                    ctx.rotate(sprinkle.rotation * Math.PI / 180);
                    ctx.fillStyle = sprinkle.color;
                    ctx.shadowBlur = 0;
                    // Sprinkle pill shape
                    const sw = sprinkle.size * 0.5;
                    const sh = sprinkle.size * 1.8;
                    ctx.beginPath();
                    roundRect(ctx, -sw / 2, -sh / 2, sw, sh, sw / 2);
                    ctx.fill();
                    ctx.restore();
                }
            });
        }
        ctx.restore();
    };
    // Update shooting star
    const updateShootingStar = (star, _width, _height) => {
        star.life++;
        // Move along angle
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;
        // Add trail points
        if (star.life % 2 === 0) {
            star.trail.unshift({
                x: star.x,
                y: star.y,
                opacity: 1,
                size: star.width,
            });
            // Limit trail length
            if (star.trail.length > 40) {
                star.trail.pop();
            }
        }
        // Fade trail points
        star.trail.forEach((point, i) => {
            point.opacity = Math.max(0, 1 - (i / star.trail.length) * 1.2);
            point.size = star.width * (1 - (i / star.trail.length) * 0.7);
        });
        // Spawn sparkles occasionally
        if (Math.random() < 0.15 && star.fadePhase === 'visible') {
            star.sparkles.push({
                x: star.x + (Math.random() - 0.5) * 20,
                y: star.y + (Math.random() - 0.5) * 20,
                size: 1 + Math.random() * 2,
                opacity: 0.7 + Math.random() * 0.3,
                life: 0,
            });
        }
        // Update sparkles
        star.sparkles.forEach(s => {
            s.life++;
            s.opacity *= 0.92;
            s.size *= 0.95;
        });
        star.sparkles = star.sparkles.filter(s => s.opacity > 0.05);
        // Keep full opacity - no fade, star disappears when off screen
        star.opacity = 0.9;
        star.fadePhase = 'visible';
    };
    // Draw realistic shooting star
    const drawShootingStar = (ctx, star) => {
        ctx.save();
        // Draw trail first (behind the head)
        if (star.trail.length > 1) {
            // Create gradient trail
            const lastPoint = star.trail[star.trail.length - 1];
            if (lastPoint) {
                const trailGradient = ctx.createLinearGradient(lastPoint.x, lastPoint.y, star.x, star.y);
                trailGradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
                trailGradient.addColorStop(0.3, `rgba(200, 220, 255, ${star.opacity * 0.2})`);
                trailGradient.addColorStop(0.7, `rgba(220, 240, 255, ${star.opacity * 0.5})`);
                trailGradient.addColorStop(1, `rgba(255, 255, 255, ${star.opacity * 0.8})`);
                ctx.strokeStyle = trailGradient;
                ctx.lineWidth = star.width;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                // Draw smooth trail
                ctx.beginPath();
                ctx.moveTo(star.trail[0].x, star.trail[0].y);
                for (let i = 1; i < star.trail.length; i++) {
                    const point = star.trail[i];
                    const prev = star.trail[i - 1];
                    const midX = (prev.x + point.x) / 2;
                    const midY = (prev.y + point.y) / 2;
                    ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
                }
                ctx.stroke();
            }
        }
        // Draw sparkles
        star.sparkles.forEach(sparkle => {
            ctx.globalAlpha = sparkle.opacity * star.opacity;
            ctx.fillStyle = '#ffffff';
            ctx.shadowBlur = sparkle.size * 3;
            ctx.shadowColor = 'rgba(200, 220, 255, 0.8)';
            ctx.beginPath();
            ctx.arc(sparkle.x, sparkle.y, Math.max(0.1, sparkle.size), 0, Math.PI * 2);
            ctx.fill();
        });
        // Draw head glow
        ctx.globalAlpha = star.opacity;
        // Outer glow - brighter and larger
        const glowGradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.headGlow * 3);
        glowGradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
        glowGradient.addColorStop(0.2, `rgba(220, 240, 255, ${star.opacity * 0.7})`);
        glowGradient.addColorStop(0.5, `rgba(180, 210, 255, ${star.opacity * 0.4})`);
        glowGradient.addColorStop(0.8, `rgba(150, 190, 255, ${star.opacity * 0.15})`);
        glowGradient.addColorStop(1, 'rgba(100, 150, 255, 0)');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.headGlow * 3, 0, Math.PI * 2);
        ctx.fill();
        // Bright core - larger and more visible
        ctx.shadowBlur = star.headGlow * 1.5;
        ctx.shadowColor = 'rgba(220, 240, 255, 1)';
        ctx.fillStyle = star.color;
        ctx.beginPath();
        ctx.arc(star.x, star.y, Math.max(0.5, star.width * 2), 0, Math.PI * 2);
        ctx.fill();
        // Central bright point
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(star.x, star.y, Math.max(0.3, star.width), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    };
    const drawStaticBackground = (ctx, width, height, isDark) => {
        if (isDark) {
            ctx.fillStyle = '#1a1f2e';
            ctx.fillRect(0, 0, width, height);
            // Static star shapes (plus/cross style)
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.lineWidth = 1;
            ctx.lineCap = 'round';
            for (let i = 0; i < 50; i++) {
                const x = Math.random() * width;
                const y = Math.random() * height;
                const size = 1.5 + Math.random() * 2;
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(Math.random() * Math.PI);
                ctx.beginPath();
                ctx.moveTo(-size, 0);
                ctx.lineTo(size, 0);
                ctx.moveTo(0, -size);
                ctx.lineTo(0, size);
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(0, 0, size * 0.2, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            }
        }
        else {
            // Birthday cake gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, height);
            gradient.addColorStop(0, '#FFF8F0');
            gradient.addColorStop(0.5, '#FFF5EB');
            gradient.addColorStop(1, '#FFF5F0');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);
            // Static sprinkles
            for (let i = 0; i < 80; i++) {
                const colorIdx = Math.floor(Math.random() * SPRINKLE_COLORS.length);
                ctx.fillStyle = SPRINKLE_COLORS[colorIdx] ?? 'rgba(255, 150, 170, 0.85)';
                ctx.globalAlpha = 0.4 + Math.random() * 0.4;
                ctx.save();
                ctx.translate(Math.random() * width, Math.random() * height);
                ctx.rotate(Math.random() * Math.PI * 2);
                const w = 2 + Math.random() * 2;
                const h = w * 2.5;
                ctx.beginPath();
                roundRect(ctx, -w / 2, -h / 2, w, h, w / 2);
                ctx.fill();
                ctx.restore();
            }
            ctx.globalAlpha = 1;
        }
    };
    return (_jsx("canvas", { ref: canvasRef, className: "animated-background", "aria-hidden": "true" }));
}
